import { useParams } from 'react-router';
import { CopyButton } from '../components/ag-grid/cellRenderers';
import { ContentSkeleton } from '../components/ContentSkeleton';
import { RunHeader } from '../components/RunHeader';
import { type ConfigJobType, useJobConfig } from '../hooks/useJobConfig';
import { HttpError } from '../utils/httpError';
import { dumpYaml } from '../utils/yaml';
import '../index.css';

/** Describe a saved config load failure. */
function configErrorMessage(error: Error): string {
  if (error instanceof HttpError) {
    if (error.status === 404) {
      return 'No saved configuration was found for this run. Imported runs and older jobs may not have one.';
    }
    if (error.status === 403) {
      return "You do not have permission to view this run's configuration.";
    }
  }
  return error.message;
}

/**
 * Header for scan config pages. Results uses a full document load because
 * the Scout viewer ships its own global stylesheet.
 */
function ScanConfigHeader({ scanId }: { scanId: string }) {
  return (
    <div className="pane-header">
      <div className="flex min-w-0 max-w-full items-center gap-0.5">
        <h1 className="min-w-0 break-all font-mono text-xs font-medium leading-4 text-ink">
          {scanId}
        </h1>
        <CopyButton value={scanId} />
      </div>
      <span className="pane-meta">config</span>
      <div className="pane-actions">
        <a
          href={`/scan/${encodeURIComponent(scanId)}`}
          className="btn btn-ghost no-underline"
        >
          Results
        </a>
      </div>
    </div>
  );
}

/** Display a job's submitted config as read-only YAML. */
export default function JobConfigPage({ jobType }: { jobType: ConfigJobType }) {
  const { jobId = '' } = useParams<{ jobId: string }>();
  const { config, error } = useJobConfig(jobType, jobId || null);

  return (
    <div className="pane flex h-full flex-col overflow-hidden">
      {jobType === 'eval-set' ? (
        <RunHeader evalSetId={jobId} current="config" />
      ) : (
        <ScanConfigHeader scanId={jobId} />
      )}
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {error ? (
          <p className="text-sm text-err">{configErrorMessage(error)}</p>
        ) : config ? (
          <pre className="overflow-x-auto rounded bg-surface-2 p-4 font-mono text-xs leading-5 text-ink">
            {dumpYaml(config)}
          </pre>
        ) : (
          <ContentSkeleton message="Loading config…" />
        )}
      </div>
    </div>
  );
}
